/**
 * Features Pinia Store
 *
 * Orchestrates MVI: intents <-> reducer <-> effects.
 * app-shell-work-context-R04/R07; business-model-features-R08/R11.
 */

import { ref, computed } from "vue";
import { defineStore } from "pinia";
import type {
  ICreateFeatureRequest,
  IUpdateFeatureRequest,
  TFeatureId,
} from "@/shared/types";
import type { IFeaturesState } from "./state";
import {
  createInitialState,
  selectFeatures,
  selectProjectId,
  selectFeaturesWithArchived,
  selectSelectorOptions,
  selectSelectedFeature,
  selectSelectedFeatureId,
  selectIsLoading,
  selectIsCreating,
  selectIsSaving,
  selectIsDeleting,
  selectError,
} from "./state";
import type { TFeaturesIntent } from "./intents";
import { intents } from "./intents";
import { featuresReducer } from "./reducer";
import * as effects from "./effects";

export const useFeaturesStore = defineStore("features", () => {
  // ========================================================================
  // State
  // ========================================================================

  const state = ref<IFeaturesState>(createInitialState());

  // ========================================================================
  // Dispatch
  // ========================================================================

  function dispatch(intent: TFeaturesIntent): void {
    state.value = featuresReducer(state.value, intent);
  }

  // ========================================================================
  // Computed (Selectors)
  // ========================================================================

  const features = computed(() => selectFeatures(state.value));
  /** Проект загруженного перечня (R10). */
  const projectId = computed(() => selectProjectId(state.value));
  const featuresWithArchived = computed(() =>
    selectFeaturesWithArchived(state.value),
  );
  const selectorOptions = computed(() => selectSelectorOptions(state.value));
  const selectedFeature = computed(() => selectSelectedFeature(state.value));
  const selectedFeatureId = computed(() => selectSelectedFeatureId(state.value));
  const isLoading = computed(() => selectIsLoading(state.value));
  const isCreating = computed(() => selectIsCreating(state.value));
  const isSaving = computed(() => selectIsSaving(state.value));
  const isDeleting = computed(() => selectIsDeleting(state.value));
  const error = computed(() => selectError(state.value));

  // ========================================================================
  // Actions
  // ========================================================================

  let listRevision = 0;
  function cancelFeatureLoad(): void {
    listRevision += 1;
    dispatch(intents.loadFeaturesCancelled());
  }

  async function loadFeatures(projectId: string): Promise<void> {
    const revision = ++listRevision;
    const selectedId = state.value.projectId === projectId ? state.value.selectedFeatureId : '';
    await effects.loadFeatures(dispatch, projectId, selectedId, () => revision === listRevision);
  }

  /** Перечень с архивными — только для поверхности управления фичами (R28). */
  async function loadFeaturesWithArchived(projectId: string): Promise<void> {
    await effects.loadFeaturesWithArchived(dispatch, projectId);
  }

  function selectFeature(featureId: string): void {
    cancelFeatureLoad();
    dispatch(intents.selectFeature(featureId));
  }

  async function createFeature(
    projectId: string,
    request: ICreateFeatureRequest,
    canReadList = true,
  ): Promise<void> {
    // frontend-screens-app-shell-R12: creation grants no list privilege.
    await effects.createFeature(dispatch, projectId, request, canReadList);
  }

  function beginContextVerification(): void {
    cancelFeatureLoad();
    dispatch(intents.contextPending());
  }
  function applyVerifiedContext(projectId: string, featureId: string): void {
    cancelFeatureLoad();
    dispatch(intents.contextVerified(projectId, featureId));
  }

  async function updateFeature(
    projectId: string,
    featureId: TFeatureId,
    request: IUpdateFeatureRequest,
  ): Promise<void> {
    await effects.updateFeature(dispatch, projectId, featureId, request);
  }

  async function deleteFeature(
    projectId: string,
    featureId: TFeatureId,
  ): Promise<void> {
    await effects.deleteFeature(dispatch, projectId, featureId);
  }

  function clearFeatures(): void {
    cancelFeatureLoad();
    dispatch(intents.clearFeatures());
  }

  // ========================================================================
  // Return
  // ========================================================================

  return {
    // State (readonly)
    state: computed(() => state.value),
    contextPending: computed(() => state.value.contextPending === true),

    // Computed
    features,
    projectId,
    featuresWithArchived,
    selectorOptions,
    selectedFeature,
    selectedFeatureId,
    isLoading,
    isCreating,
    isSaving,
    isDeleting,
    error,

    // Actions
    loadFeatures,
    cancelFeatureLoad,
    loadFeaturesWithArchived,
    selectFeature,
    createFeature,
    updateFeature,
    deleteFeature,
    clearFeatures,
    beginContextVerification,
    applyVerifiedContext,
  };
});
